import { USGSMonitoringStationDevice } from 'features/Integrations/features/ConnectedDevices/types/USGSMONITORINGTYPE';
import { usgsGetMostRecentValueForParam } from 'features/Integrations/features/ConnectedDevices/utils/usgsGeneralUtils';
import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { UsgsMonitoringStationItem } from '../../../../types/MonitoringStation_types';
import USGSMarker from '../components/USGSMarker';

/**
 * @param device  - Device object used to determine current threshold level.
 * @returns {string} a string defining the threshold level
 *
 * returns noFloodStages if thresholds are not established or data is unavailable
 * returns notFlooding if below all thresholds
 * returns major, moderate, minor, or action if in that flood stage
 */
export function currentThreshold(device: USGSMonitoringStationDevice | any): string {
  if (!device || !device.data || !device.flood_thresholds || device.data.length === 0) {
    return 'noFloodStages';
  }

  if (device.flood_thresholds?.not_established) {
    return 'noFloodStages';
  }

  //Need to look for the most recent data entry of either gage height or discharge
  // Parameter code 00065 is gage height
  // Parameter code 00060 is discharge
  const gageHeightEntry: number | null =
    usgsGetMostRecentValueForParam(device, 'Gage height').value ?? null;
  const dischargeEntry: number | null =
    usgsGetMostRecentValueForParam(device, 'Discharge').value ?? null;

  const priorityOrder: string[] = ['major', 'moderate', 'minor', 'action'];

  // If none of the priority thresholds contain a valid (>=0) flow or stage
  // value (e.g., all are missing or set to sentinel -9999), treat as
  // "noFloodStages" — thresholds not established in practice.
  const hasAnyValidThreshold = priorityOrder.some((priority) => {
    const p = device.flood_thresholds[priority];
    if (!p) return false;
    const flow = p.flow;
    const stage = p.stage;
    const validFlow = typeof flow === 'number' && flow >= 0;
    const validStage = typeof stage === 'number' && stage >= 0;
    return validFlow || validStage;
  });

  if (!hasAnyValidThreshold) {
    return 'noFloodStages';
  }

  for (const priority of priorityOrder) {
    if (!device.flood_thresholds[priority]) {
      continue;
    }
    const thresholdName: string = priority;
    const thresholdflow: number = device.flood_thresholds[priority].flow;
    const thresholdstage: number = device.flood_thresholds[priority].stage;

    //Check if either flow or stage is a valid number | invalid numbers are negative
    const validflow: boolean = typeof thresholdflow === 'number' && thresholdflow >= 0;
    const validstage: boolean = typeof thresholdstage === 'number' && thresholdstage >= 0;
    if (
      (validflow && dischargeEntry !== null && dischargeEntry >= thresholdflow) ||
      (validstage && gageHeightEntry !== null && gageHeightEntry >= thresholdstage)
    ) {
      // Return first threshold met based on priority order
      return thresholdName;
    }
  }

  //This only returns if no thresholds are met
  return 'notFlooding';
}

/**
 * Return an array of flood threshold keys present on the device that contain
 * at least one valid numeric value (flow or stage).
 *
 * Example return: ['major','minor']
 */
export function getAvailableFloodThresholds(device: USGSMonitoringStationDevice | any): string[] {
  if (!device || !device.flood_thresholds) return [];

  const possible = ['major', 'moderate', 'minor', 'action'];
  const result: string[] = [];

  for (const key of possible) {
    const entry = device.flood_thresholds[key];
    if (!entry) continue;
    const flow = entry.flow;
    const stage = entry.stage;
    const validFlow = typeof flow === 'number' && flow >= 0;
    const validStage = typeof stage === 'number' && stage >= 0;
    if (validFlow || validStage) result.push(key);
  }

  return result;
}

//Flood stage meanings can be found on https://www.weather.gov/lot/hydrology_definitions
// Action stage: Use Cauytion. Flooding is possible.
// Minor flood stage: Minimal or no property damage, but possibly some public threat or inconvenience.
// Moderate flood stage: Some inundation of structures and roads near stream.
// Major flood stage: Extensive inundation of structures and roads.

/**
 * Render a USGS SVG marker to an SVG data-URL which can be used as a Leaflet `iconUrl`.
 *
 * The function builds props for the `USGSMarker` component, renders it to static SVG
 * markup with `renderToStaticMarkup`, then encodes the SVG as a `data:image/svg+xml` URL.
 *
 * @param {UsgsMonitoringStationItem} device - Device object used to derive marker props (type/state/size). When omitted, sensible defaults are used.
 * @param {string|any|undefined} className - Optional CSS class forwarded to the `USGSMarker` component.
 * @param {any} rest - Additional props forwarded to the `USGSMarker` component.
 * @returns {string} A `data:image/svg+xml` URL containing the rendered SVG
 * @example
 * // returned value can be passed to L.icon({ iconUrl }) or to existing `imageToIcon` helper
 * const url = makeUSGSMarker(device);
 */
export function makeUSGSMarker(
  device: UsgsMonitoringStationItem | any,
  className: string | any | undefined,
  rest: any,
): string {
  const siteTypeRaw: string | null = device?.site_type ?? null;
  const type =
    siteTypeRaw !== null
      ? normalizeSiteType(siteTypeRaw)
      : mapSiteTypeCodeToType(device?.site_type_code);

  const state = currentThreshold(device) || 'noFloodStages';

  // Build props and element as named consts for logging, then render to a data URL
  const usgsMarkerProps = { type, state, className, ...rest };
  const usgsMarkerElement = React.createElement(USGSMarker, usgsMarkerProps);
  const usgsMarkerSvg = renderToStaticMarkup(usgsMarkerElement);
  const usgsMarkerDataUrl = 'data:image/svg+xml;utf8,' + encodeURIComponent(usgsMarkerSvg);
  return usgsMarkerDataUrl;
}

/**
 * Normalize a USGS site_type string (display name or code) to a USGSMarker type key.
 *
 * Handles both full API display names ('Lake, Pond, or Reservoir') and short
 * codes ('LK'), defaulting to 'stream' for unrecognized values.
 *
 * @param {string|undefined|null} siteType - Raw site_type from the USGS API
 * @returns {string} USGSMarker `type` (one of 'lake','estuary','stream','wetland','coastal')
 */
export function normalizeSiteType(siteType: string | null | undefined): string {
  if (!siteType) return 'stream';
  const lower = siteType.toLowerCase().trim();
  if (lower === 'stream' || lower === 'st') return 'stream';
  if (lower.startsWith('lake') || lower === 'lk') return 'lake';
  if (lower.startsWith('estuary') || lower === 'es') return 'estuary';
  if (lower.startsWith('wetland') || lower === 'we') return 'wetland';
  if (lower.startsWith('coastal') || lower.startsWith('ocean') || lower === 'oc-co')
    return 'coastal';
  return 'stream';
}

/**
 * Map a USGS `site_type_code` to a USGSMarker `type` string.
 *
 * Recognized mappings:
 * - `LK` -> `lake`
 * - `ES` -> `estuary`
 * - `ST` -> `stream`
 * - `WE` -> `wetland`
 * - `OC-CO` -> `coastal`
 *
 * If the input is falsy or not one of the recognized codes, this function
 * returns `stream`.
 *
 * @param {string|undefined|null} site_type_code - USGS site type code (e.g. 'LK')
 * @returns {string} USGSMarker `type` (one of 'lake','estuary','stream','wetland','coastal')
 */
export function mapSiteTypeCodeToType(site_type_code: string | undefined | null): string {
  if (!site_type_code) return 'stream';
  const code = String(site_type_code).toUpperCase();
  switch (code) {
    case 'LK':
      return 'lake';
    case 'ES':
      return 'estuary';
    case 'ST':
      return 'stream';
    case 'WE':
      return 'wetland';
    case 'OC-CO':
      return 'coastal';
    default:
      return 'stream'; // default to 'stream' if unrecognized code
  }
}
